// src/mechanism/cookie-engine.js
import { Storage } from './storage';
import { URL_CONFIG } from '@/url_config'; // 引入你的服务器配置
import { CapacitorCookies } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';

const COOKIE_PREFIX = 'v_cookie_';
const INTERNAL_SHARED_DOMAIN = '__app_internal_services__'; // 核心：专门为内部微服务开辟的虚拟共享域

// 🌟 核心优化：动态检查环境，防止单页面应用执行过快导致错判
// 包含双重保险：检查注入变量，或检查原生桥接对象是否存在
const checkIsHarmony = () => window.__IS_HARMONY__ === true || !!window.HarmonyJSBridge;

export const CookieEngine = {
    /**
     * AOT 预注入机制 (Ahead-of-Time)
     */
    async syncToAllNativeDomains() {
        const isHarmonyEnv = checkIsHarmony();

        // 如果既不是 Capacitor 原生，也不是鸿蒙，直接放行 (比如纯 H5 浏览器)
        if (!Capacitor.isNativePlatform() && !isHarmonyEnv) return;

        try {
            const sharedCookies = await this.findFromStore(INTERNAL_SHARED_DOMAIN);
            if (!sharedCookies || Object.keys(sharedCookies).length === 0) return;

            const domains = Object.values(URL_CONFIG).map(url => new URL(url).origin);
            const uniqueDomains = [...new Set(domains)]; // 去重

            for (const origin of uniqueDomains) {
                for (const [key, value] of Object.entries(sharedCookies)) {
                    // 【鸿蒙分支】：调用 ArkTS 桥接
                    if (isHarmonyEnv) {
                        await window.HarmonyJSBridge.setCookie(origin, key, value);
                    }
                    // 【iOS / Android 分支】：保持原汁原味的 Capacitor 逻辑
                    else if (Capacitor.isNativePlatform()) {
                        await CapacitorCookies.setCookie({
                            url: origin,
                            key: key,
                            value: value
                        });
                    }
                }
            }
            console.log(`[CookieEngine] 🚀 AOT同步完毕`);
        } catch (error) {
            console.error('[CookieEngine] AOT同步失败:', error);
        }
    },

    /**
     * 清空内部虚拟共享域的 Cookie
     */
    async clearInternalCookies() {
        // 1. 清空 JS 层的虚拟共享池
        const storageKey = `${COOKIE_PREFIX}${INTERNAL_SHARED_DOMAIN}`;
        await Storage.set(storageKey, {});
        console.log(`[CookieEngine] ♻️ 已清空内部虚拟共享域 (${INTERNAL_SHARED_DOMAIN}) 的残存 Cookie`);

        const isHarmonyEnv = checkIsHarmony();

        // 2. 清理底层 OS 缓存
        // 【鸿蒙分支】
        if (isHarmonyEnv) {
            try {
                await window.HarmonyJSBridge.clearAllCookies();
                console.log(`[CookieEngine] 🧹 已彻底清空鸿蒙原生系统缓存的 Cookie`);
            } catch (error) {
                console.error('[CookieEngine] 鸿蒙原生 Cookie 清理失败:', error);
            }
        }
        // 【iOS / Android 分支】
        else if (Capacitor.isNativePlatform()) {
            try {
                await CapacitorCookies.clearAllCookies();
                console.log(`[CookieEngine] 🧹 已彻底清空原生系统 (iOS/Android) 缓存的 Cookie`);
            } catch (error) {
                console.error('[CookieEngine] 清空原生 Cookie 失败:', error);
            }
        }
    },

    /**
     * 判断字符串是否为 IPv4 地址
     */
    isIpAddress(hostname) {
        return /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(hostname);
    },

    /**
     * 判断当前请求的 URL 是否属于我们自己的内部微服务
     * @param {string} url
     * @returns {boolean}
     */
    isInternalService(url) {
        return Object.values(URL_CONFIG).some(baseUrl => url.startsWith(baseUrl));
    },

    /**
     * 解析并保存 Set-Cookie
     */
    async processSetCookie(requestUrl, headers) {
        if (!headers) return;

        const setCookieHeader = headers['set-cookie'] || headers['Set-Cookie'] || headers['SET-COOKIE'];
        if (!setCookieHeader) return;

        let cookiesToProcess = Array.isArray(setCookieHeader) ? setCookieHeader : [setCookieHeader];
        const currentHostname = new URL(requestUrl).hostname;
        const isIp = this.isIpAddress(currentHostname);

        for (const cookieStr of cookiesToProcess) {
            const parsed = this.parseCookieString(cookieStr);
            if (!parsed.key) continue;

            let targetDomain = parsed.attributes.Domain || currentHostname;
            if (isIp) {
                targetDomain = currentHostname;
            }

            if (this.isInternalService(requestUrl)) {
                targetDomain = INTERNAL_SHARED_DOMAIN;
                console.log(`[CookieEngine] 识别到内部服务，Cookie: ${parsed.key} 已重定向保存至共享池`);
            } else {
                console.log(`[CookieEngine] 已保存外部 Cookie: ${parsed.key} 至域: ${targetDomain}`);
            }

            await this.saveToStore(targetDomain, parsed.key, parsed.value);
        }
    },

    /**
     * 根据请求的 URL 查找匹配的所有 Cookie
     */
    async getMatchedCookies(requestUrl) {
        const hostname = new URL(requestUrl).hostname;
        let allCookies = {};

        if (this.isInternalService(requestUrl)) {
            allCookies = await this.findFromStore(INTERNAL_SHARED_DOMAIN);
        } else {
            if (this.isIpAddress(hostname)) {
                allCookies = await this.findFromStore(hostname);
            } else {
                const parts = hostname.split('.');
                const minParts = hostname === 'localhost' ? 1 : 2;

                for (let i = 0; i <= parts.length - minParts; i++) {
                    const domain = parts.slice(i).join('.');
                    const cookiesInDomain = await this.findFromStore(domain);
                    allCookies = { ...allCookies, ...cookiesInDomain };
                }
            }
        }

        const cookieArray = Object.entries(allCookies).map(([k, v]) => `${k}=${v}`);
        return cookieArray.join('; ');
    },

    // ==========================================
    // --- 内部私有方法 ---
    // ==========================================
    async saveToStore(domain, key, value) {
        const cleanDomain = domain.startsWith('.') ? domain.substring(1) : domain;
        const storageKey = `${COOKIE_PREFIX}${cleanDomain}`;
        const domainCookies = (await Storage.get(storageKey)) || {};
        domainCookies[key] = value;
        await Storage.set(storageKey, domainCookies);
    },

    async findFromStore(domain) {
        const cleanDomain = domain.startsWith('.') ? domain.substring(1) : domain;
        const storageKey = `${COOKIE_PREFIX}${cleanDomain}`;
        return (await Storage.get(storageKey)) || {};
    },

    parseCookieString(str) {
        const parts = str.split(';').map(p => p.trim());
        if (parts.length === 0) return {};
        const mainPair = parts[0];
        const firstEqIndex = mainPair.indexOf('=');
        if (firstEqIndex === -1) return {};

        const key = mainPair.substring(0, firstEqIndex).trim();
        const value = mainPair.substring(firstEqIndex + 1).trim();
        const attributes = {};
        for (let i = 1; i < parts.length; i++) {
            const pair = parts[i];
            const eqIdx = pair.indexOf('=');
            if (eqIdx !== -1) {
                const attrKey = pair.substring(0, eqIdx).trim();
                const attrVal = pair.substring(eqIdx + 1).trim();
                attributes[attrKey.charAt(0).toUpperCase() + attrKey.slice(1).toLowerCase()] = attrVal;
            } else {
                attributes[pair.charAt(0).toUpperCase() + pair.slice(1).toLowerCase()] = true;
            }
        }
        return { key, value, attributes };
    },

    async clearAllAppCookies() {
        const isHarmonyEnv = checkIsHarmony();

        // 1. 清理底层 OS 缓存 (复用之前的逻辑)
        if (isHarmonyEnv) {
            try {
                await window.HarmonyJSBridge.clearAllCookies();
                console.log(`[CookieEngine] 🧹 已彻底清空鸿蒙原生系统缓存的 Cookie`);
            } catch (error) {
                console.error('[CookieEngine] 鸿蒙原生 Cookie 清理失败:', error);
            }
        } else if (Capacitor.isNativePlatform()) {
            try {
                await CapacitorCookies.clearAllCookies();
                console.log(`[CookieEngine] 🧹 已彻底清空原生系统 (iOS/Android) 缓存的 Cookie`);
            } catch (error) {
                console.error('[CookieEngine] 清空原生 Cookie 失败:', error);
            }
        }

        // 2. 清理 JS 层所有相关的虚拟共享池
        try {
            // 获取 Storage 中所有的 key
            const allKeys = await Storage.keys();

            // 筛选出所有以 COOKIE_PREFIX ('v_cookie_') 开头的键并清空
            const cookieKeys = allKeys.filter(key => key.includes(COOKIE_PREFIX));

            for (const key of cookieKeys) {
                await Storage.removeRaw(key);
                // await Storage.remove(key);
            }
            console.log(`[CookieEngine] ♻️ 已清空 JS 层所有的虚拟域 Cookie (共 ${cookieKeys.length} 个域)`);
        } catch (error) {
            console.error('[CookieEngine] 清空 JS 层虚拟域 Cookie 失败:', error);
        }
    },
};